import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localizedPath } from '@/lib/contactApi';

export interface CorporateBrochureFormData {
  company: string;
  department: string;
  name: string;
  email: string;
  phone: string;
  industry: string;
  format: string;
  participants: string;
  timeline: string;
  message: string;
  privacyAgreed: boolean;
}

const initialFormData: CorporateBrochureFormData = {
  company: '',
  department: '',
  name: '',
  email: '',
  phone: '',
  industry: '',
  format: '',
  participants: '',
  timeline: '',
  message: '',
  privacyAgreed: false,
};

export default function BrochureForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CorporateBrochureFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const industryOptions = [
    { value: '', label: t('corporateBrochure.form.industry.select') },
    { value: 'hotel', label: t('corporateBrochure.form.industry.hotel') },
    { value: 'residence', label: t('corporateBrochure.form.industry.residence') },
    { value: 'finance', label: t('corporateBrochure.form.industry.finance') },
    { value: 'retail', label: t('corporateBrochure.form.industry.retail') },
    { value: 'luxury-sales', label: t('corporateBrochure.form.industry.luxurySales') },
    { value: 'clinic', label: t('corporateBrochure.form.industry.clinic') },
    { value: 'automotive', label: t('corporateBrochure.form.industry.automotive') },
    { value: 'chauffeur', label: t('corporateBrochure.form.industry.chauffeur') },
    { value: 'other', label: t('corporateBrochure.form.industry.other') },
  ];

  const formatOptions = [
    { value: '', label: t('corporateBrochure.form.format.select') },
    { value: 'dispatch', label: t('corporateBrochure.form.format.dispatch') },
    { value: 'onsite', label: t('corporateBrochure.form.format.onsite') },
    { value: 'undecided', label: t('corporateBrochure.form.format.undecided') },
  ];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.company.trim()) newErrors.company = t('corporateBrochure.form.company.error');
    if (!formData.name.trim()) newErrors.name = t('corporateBrochure.form.name.error');
    if (!formData.email.trim()) {
      newErrors.email = t('corporateBrochure.form.email.error');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('corporateBrochure.form.email.invalid');
    }
    if (!formData.industry) newErrors.industry = t('corporateBrochure.form.industry.error');
    if (formData.message.length > 500) newErrors.message = t('corporateBrochure.form.message.error');
    if (!formData.privacyAgreed) newErrors.privacyAgreed = t('corporateBrochure.form.privacyError');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      navigate(localizedPath('/corporate/brochure/confirm'), { state: { formData } });
    }
  };

  const updateField = (field: keyof CorporateBrochureFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/40" id="corporate-brochure-form">
      <div className="max-w-[700px] mx-auto px-8">
        <div className="text-center mb-14">
          {String(t('corporateBrochure.form.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('corporateBrochure.form.title')}
          </h2>
          {String(t('corporateBrochure.form.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">{__s}</p>))}
          <div className="gold-line mx-auto mb-5"></div>
          {String(t('corporateBrochure.form.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-600 tracking-[0.04em] leading-relaxed">{__s}</p>))}
        </div>

        <div className="text-center mb-10 bg-background-50 border border-background-200/60 p-5">
          {String(t('corporateBrochure.form.noHardSell')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">{__s}</p>))}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5">
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.company')} <span className="text-accent-600 text-[12px]">{t('corporateBrochure.form.required')}</span>
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={(e) => updateField('company', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateBrochure.form.company.placeholder')}
              />
              {errors.company && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.company}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.department')} <span className="text-foreground-400 text-[12px]">{t('corporateBrochure.form.optional')}</span>
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={(e) => updateField('department', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateBrochure.form.department.placeholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.name')} <span className="text-accent-600 text-[12px]">{t('corporateBrochure.form.required')}</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateBrochure.form.name.placeholder')}
              />
              {errors.name && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.email')} <span className="text-accent-600 text-[12px]">{t('corporateBrochure.form.required')}</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateBrochure.form.email.placeholder')}
              />
              {errors.email && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.phone')} <span className="text-foreground-400 text-[12px]">{t('corporateBrochure.form.optional')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateBrochure.form.phone.placeholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.industry')} <span className="text-accent-600 text-[12px]">{t('corporateBrochure.form.required')}</span>
              </label>
              <select
                name="industry"
                value={formData.industry}
                onChange={(e) => updateField('industry', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 cursor-pointer appearance-none"
              >
                {industryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.industry && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.industry}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.participants')} <span className="text-foreground-400 text-[12px]">{t('corporateBrochure.form.optional')}</span>
              </label>
              <input
                type="text"
                name="participants"
                value={formData.participants}
                onChange={(e) => updateField('participants', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateBrochure.form.participants.placeholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.format')} <span className="text-foreground-400 text-[12px]">{t('corporateBrochure.form.optional')}</span>
              </label>
              <select
                name="format"
                value={formData.format}
                onChange={(e) => updateField('format', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 cursor-pointer appearance-none"
              >
                {formatOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.timeline')} <span className="text-foreground-400 text-[12px]">{t('corporateBrochure.form.optional')}</span>
              </label>
              <input
                type="text"
                name="timeline"
                value={formData.timeline}
                onChange={(e) => updateField('timeline', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateBrochure.form.timeline.placeholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateBrochure.form.message')}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => updateField('message', e.target.value)}
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400 resize-none"
                placeholder={t('corporateBrochure.form.message.placeholder')}
              ></textarea>
              <p className="text-[13px] text-foreground-400 mt-1 text-right">
                {formData.message.length}/500
              </p>
              {errors.message && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.message}</p>
              )}
            </div>

            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="privacyAgreed"
                  checked={formData.privacyAgreed}
                  onChange={(e) => updateField('privacyAgreed', e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-colors duration-200 ${
                    formData.privacyAgreed
                      ? 'bg-accent-500 border-accent-500'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {formData.privacyAgreed && (
                    <i className="ri-check-line text-[12px] text-primary-900"></i>
                  )}
                </div>
                <span className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
                  {t('corporateBrochure.form.privacyAgree')}{' '}
                  <Link to="/privacy" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors">
                    {t('corporateBrochure.form.privacy')}
                  </Link>
                  <span className="text-accent-600 text-[12px] ml-1">{t('corporateBrochure.form.required')}</span>
                </span>
              </label>
              {errors.privacyAgreed && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.privacyAgreed}</p>
              )}
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="btn-glow w-full py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('corporateBrochure.form.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
